import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import debounce from 'lodash/debounce';
import { v4 as uuidv4 } from 'uuid';

import { usePrintsUiStore } from './use-prints-ui-store';
import { useAdvancedEditorStore } from './use-advanced-editor-store';
import { usePrintsConfigStore } from './use-prints-config-store';
import { processOrderPrints } from '../utils/basket-helpers';
import { listDuplicatePhotos } from '../common/duplicate-detection/list-duplicate-photos';
import { UpdatePhotoEvents } from '../common/events/event-declarations';
import { clonePhotoWithoutEdits } from '../utils/photo-utils';
import ViewingSizeKey from '../mobile/viewing-size-key';
import { PHOTO_ATTRIBUTES } from '../constants';
import { selectSizesWithPhotos } from './selectors';
import { buildSaveOrderPayload } from '../utils/build-save-order-payload';
import SizeHelpers from '../common/size-helpers';

// Debounced auto-save for background updates.
const debouncedSaveOrder = debounce(() => {
  if (usePrintsUiStore.getState().autoSaveEnabled) {
    usePrintsOrderStore.getState().saveOrder();
  }
}, 2000);

let saveQueued = false;

export const usePrintsOrderStore = create(
  immer((set, get) => ({
    // --- STATE ---
    photos: [], // user photo data.
    sizesBeforeUpload: [], // clone of print sizes with photos structure before upload batch. Used for duplicate detection.

    // --- ACTIONS ---

    // Load order data - called from config store
    loadBasket: async (basketUrl) => {
      const { allSizes, allFinishes } = usePrintsConfigStore.getState();

      const basketResponse = await fetch(`${basketUrl}?_=${Date.now()}`);
      const basketData = await basketResponse.json();
      const { prints = [] } = basketData;

      const loadedPhotos = processOrderPrints(prints, allSizes, allFinishes);

      // Assign a client_uuid and versions to all loaded photos for stable client-side identification and edit tracking.
      loadedPhotos.forEach((photo) => {
        photo.client_uuid = photo.client_uuid || uuidv4(); // eslint-disable-line camelcase
        photo.currentVersion = 1;
        photo.savedVersion = 1;
      });

      set((state) => {
        state.photos = loadedPhotos;
      });
    },

    handleAddToCart: (callback) => {
      get().saveImmediately(callback);
    },

    // --- Upload Actions ---
    uploadBegun: () => {
      const { allSizes } = usePrintsConfigStore.getState();
      set((state) => {
        state.sizesBeforeUpload = selectSizesWithPhotos(allSizes, get().photos);
      });
    },

    addUploadedPhoto: (photo) => {
      set((state) => {
        state.photos.unshift({
          ...photo,
          id: null,
          client_uuid: uuidv4(), // eslint-disable-line camelcase
          quantity: 1,
          currentVersion: 1,
          savedVersion: 0,
        });
      });
    },

    // Adds a photo immediately to photos state.
    // Returns the client_uuid assigned to the new entry so the caller can
    // reference the photo later.
    addPendingPhoto: (photo) => {
      const clientUuid = uuidv4();
      set((state) => {
        state.photos.unshift({
          ...photo,
          id: null,
          client_uuid: clientUuid, // eslint-disable-line camelcase
          quantity: 1,
          currentVersion: 1,
          savedVersion: 0,
          uploaded: false,
        });
      });

      return clientUuid;
    },

    // Sets thumb_url and marks the photo as fully uploaded.
    confirmPhotoThumbLoaded: ({ clientUuid, thumbUrl }) => {
      set((state) => {
        const photo = state.photos.find((p) => p.client_uuid === clientUuid);
        if (photo) {
          photo.thumb_url = thumbUrl; // eslint-disable-line camelcase
          photo.uploaded = true;
        }
      });
    },

    uploadFinished: () => {
      debouncedSaveOrder();
      get().checkForNewDuplicates();
    },

    removeIncompleteUploads: () => {
      // a successfully uploaded and saved photo will have the `uploaded: true` flag and an `id`, existing photos will just have an `id`
      set((state) => {
        state.photos = state.photos.filter((p) => p.uploaded || p.id);
      });
    },

    // --- Save Actions ---

    // Use for critical saves (e.g. "Add to Cart") that must finish before a callback is executed.
    // The callback is only invoked on success — if the save fails the user remains on the page
    // with their unsaved changes intact, matching legacy behaviour.
    saveImmediately: async (onComplete) => {
      // Cancel any pending auto-save, then save immediately and wait for it to finish.
      debouncedSaveOrder.cancel();
      try {
        await get().saveOrder();
        if (onComplete) {
          onComplete();
        }
      } catch {
        // Errors are already logged by saveOrder. Do not invoke the callback so the caller
        // (e.g. Add to Cart) stays on the current page rather than navigating with unsaved changes.
      }
    },

    // Public function to trigger a debounced save
    debouncedSave: () => {
      debouncedSaveOrder();
    },

    // Core action to send the current order state to the backend.
    // Should generally not be called directly, use `debouncedSaveOrder` for auto-saves or `saveImmediately` for critical saves.
    // Returns a Promise and is awaitable.
    saveOrder: async () => {
      const { setIsSaving, selectedDeliveryKey } = usePrintsUiStore.getState();
      const { urls, allSizes } = usePrintsConfigStore.getState();
      const { photos } = get();

      if (usePrintsUiStore.getState().isSaving) {
        saveQueued = true;
        return Promise.resolve();
      }

      const payload = buildSaveOrderPayload(
        photos,
        allSizes,
        selectedDeliveryKey,
      );

      // Track the photos and versions of photo that were sent to the server in this payload
      const photosInPayload = payload.sizes.flatMap((s) => s.photos);
      const versionsInPayload = new Map();
      photosInPayload.forEach((p) => {
        versionsInPayload.set(p.client_uuid, p.currentVersion);
      });

      if (!payload.sizes.length && !payload.to_be_deleted.length) {
        // Clean up photos that were deleted before being saved to the server.
        // They have no id (never reached the server) and zero quantity (deleted locally).
        // No server action is needed, but they must be removed so hasUnsavedChanges resolves correctly.
        set((state) => {
          state.photos = state.photos.filter(
            (p) => p.quantity > 0 || p.id || p.keepInUiWhenZeroQuantity,
          );
        });
        return Promise.resolve();
      }

      setIsSaving(true);

      try {
        const response = await fetch(urls.add_to_cart, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error('Failed to save order');
        const newItems = await response.json();

        set((state) => {
          // Reconcilliation - update state to match server state

          // 1. Filter out deleted photos
          state.photos = state.photos.filter(
            (p) => p.quantity > 0 || p.keepInUiWhenZeroQuantity,
          );

          photosInPayload.forEach((savedPhoto) => {
            // 2. Find each photo in state by client_uuid
            const photoInState = state.photos.find(
              (p) => p.client_uuid === savedPhoto.client_uuid,
            );

            if (photoInState) {
              // 3. Compare versions between current and last saved
              const savedVersion = versionsInPayload.get(
                photoInState.client_uuid,
              );

              // If the current version is the one we just saved, update the savedVersion.
              // If it's different, it means another edit happened during the save, so we leave it as is,
              // and the next save cycle will pick it up.
              if (photoInState.currentVersion === savedVersion) {
                photoInState.savedVersion = photoInState.currentVersion;
              }

              const newItem = newItems.find(
                (item) => item.client_uuid === savedPhoto.client_uuid,
              );

              // 4. Assign properties to new photos
              if (newItem) {
                photoInState.id = newItem.id;
                photoInState.image_id = newItem.image_id; // eslint-disable-line camelcase
                delete photoInState.make_new_copy;
              }
            }
          });
        });
      } catch (error) {
        console.error('Error saving order:', error);
        // re-throw to allow callers to handle
        throw error;
      } finally {
        setIsSaving(false);

        // save again if we've had another save queued up
        if (saveQueued) {
          saveQueued = false;
          get().saveOrder();
        }
      }
    },

    // Explicitly mark all existing photos with a quantity as modified and then call save.
    // This ensures that global changes, such as a new delivery option,
    // are persisted to the backend even if individual photo properties haven't changed.
    saveAllPhotos: () => {
      set((state) => {
        state.photos.forEach((photo) => {
          if (photo.id && photo.quantity > 0) {
            photo.currentVersion += 1;
          }
        });
      });

      debouncedSaveOrder();
    },

    // Updates the photos state to reflect a change in print finish aka sizeKey.
    // It assigns new size keys to photos corresponding to the chosen finish
    // and marks them as modified. This function *only* updates state; it does not trigger a save.
    updatePhotosToNewFinish: (finish, sizesWithPhotos) => {
      const { features, allSizes } = usePrintsConfigStore.getState();

      // If MPF is enabled then return early; a photo cannot be changed, it needs to be re-uploaded as
      // the other finish
      if (features.allowMultiplePrintFinishesPerOrder) {
        return;
      }

      const { name: newFinishName } = finish;
      if (!newFinishName) {
        return;
      }

      const { viewingSizeKey } = usePrintsUiStore.getState();

      const photosToMove = [];
      sizesWithPhotos.forEach((size) => {
        // filter for sizes that have photos and exclude new finish sizes, no need to change
        if (
          size.photos.length > 0 &&
          size.options.finish.name !== newFinishName
        ) {
          size.photos.forEach((photo) => {
            const border = photo.border;
            const targetSizeKey = new ViewingSizeKey(viewingSizeKey).create({
              size: size.size_id,
              finish: newFinishName,
              border,
            });

            if (allSizes.some((s) => s.key === targetSizeKey)) {
              photosToMove.push({ photo, targetSizeKey });
            }
          });
        }
      });

      if (photosToMove.length === 0) {
        return;
      }

      set((state) => {
        photosToMove.forEach(({ photo, targetSizeKey }) => {
          // Remove photo with same root_image_id from target size if it exists.
          const existingInTarget = state.photos.find(
            (p) =>
              p.root_image_id === photo.root_image_id &&
              p.sizeKey === targetSizeKey,
          );
          if (existingInTarget) {
            existingInTarget.quantity = 0;
            existingInTarget.currentVersion += 1;
          }

          // Find the photo to move in the current state and update its sizeKey
          const photoToUpdate = state.photos.find(
            (p) => p.image_id === photo.image_id && p.sizeKey === photo.sizeKey,
          );
          if (photoToUpdate) {
            photoToUpdate.sizeKey = targetSizeKey;
            photoToUpdate.currentVersion += 1;
          }
        });
      });
    },

    // --- Photo Actions ---
    removePhotoFromSize: ({ clientUuids }) => {
      const { setPhotoView } = usePrintsUiStore.getState();
      const { features } = usePrintsConfigStore.getState();
      const allowZeroQuantity = !features.mobile;

      set((state) => {
        clientUuids.forEach((clientUuid) => {
          const photo = state.photos.find((p) => p.client_uuid === clientUuid);
          if (photo) {
            const isLast =
              state.photos.filter(
                (p) =>
                  p.root_image_id === photo.root_image_id && p.quantity > 0,
              ).length <= 1;

            photo.quantity = 0;
            photo.currentVersion += 1;

            if (allowZeroQuantity && isLast) {
              photo.keepInUiWhenZeroQuantity = true;
            }
          }
        });
      });

      debouncedSaveOrder();
      setPhotoView();
    },

    // Remove every photo related to the same root image
    removePhotoByRoot: ({ clientUuid }) => {
      set((state) => {
        const photoToRemove = state.photos.find(
          (p) => p.client_uuid === clientUuid,
        );

        if (!photoToRemove) return;

        // If the photo is zero quantity but kept displayed, its been specifically removed so hard-delete it
        if (photoToRemove.keepInUiWhenZeroQuantity) {
          delete photoToRemove.keepInUiWhenZeroQuantity;
          photoToRemove.currentVersion += 1;
        } else {
          // Otherwise zero all instances of the same root image
          const { root_image_id: rootImageId } = photoToRemove;
          state.photos.forEach((p) => {
            if (p.root_image_id === rootImageId) {
              delete p.keepInUiWhenZeroQuantity;
              p.quantity = 0;
              p.currentVersion += 1;
            }
          });
        }
      });

      debouncedSaveOrder();
    },

    updatePhoto: ({ updatedPhotos, shouldSave }) => {
      set((state) => {
        updatedPhotos.forEach((updatedPhoto) => {
          const photoToUpdate = state.photos.find(
            (p) => p.client_uuid === updatedPhoto.client_uuid,
          );

          if (photoToUpdate) {
            // We don't know what has changed but need to be cautious about leaking irrelevant properties
            // into the photo so only update attributes that might have changed
            PHOTO_ATTRIBUTES.forEach((attr) => {
              if (Object.prototype.hasOwnProperty.call(updatedPhoto, attr)) {
                photoToUpdate[attr] = updatedPhoto[attr];
              }
            });
            photoToUpdate.currentVersion += 1;
          }
        });
      });

      if (shouldSave) {
        debouncedSaveOrder();
      }
    },

    setPhotoQuantity: ({ rootImageIds, targetSizeKey, newQuantity }) => {
      set((state) => {
        rootImageIds.forEach((rootImageId) => {
          const existingPhoto = state.photos.find(
            (p) =>
              p.root_image_id === rootImageId && p.sizeKey === targetSizeKey,
          );

          if (existingPhoto) {
            // If photo exists for this size, just update its quantity.
            if (existingPhoto.quantity !== newQuantity) {
              existingPhoto.quantity = newQuantity;
              existingPhoto.currentVersion += 1;
              delete existingPhoto.keepInUiWhenZeroQuantity;
            }
          } else if (newQuantity > 0) {
            // The photo doesn't exist in the target size at all, so create a new clone.
            let sourcePhoto = state.photos.find(
              (p) =>
                p.image_id === rootImageId && p.root_image_id === rootImageId,
            );
            if (!sourcePhoto) {
              sourcePhoto = state.photos.find(
                (p) => p.root_image_id === rootImageId,
              );
            }

            if (sourcePhoto) {
              const clientUuid = uuidv4();
              const newPhoto = {
                ...clonePhotoWithoutEdits(sourcePhoto),
                id: null,
                image_id: null, // eslint-disable-line camelcase
                root_image_id: sourcePhoto.root_image_id, // eslint-disable-line camelcase
                original_image_id: sourcePhoto.root_image_id, // eslint-disable-line camelcase
                sizeKey: targetSizeKey,
                quantity: newQuantity,
                make_new_copy: true, // eslint-disable-line camelcase
                client_uuid: clientUuid, // eslint-disable-line camelcase
                currentVersion: 1,
                savedVersion: 0,
              };
              state.photos.push(newPhoto);
            }
          }
        });
      });
      debouncedSaveOrder();
    },

    // Bulk resizes photos to a new target size key.
    // This action handles two main scenarios for resizing photos to a new target size key:
    // 1. If `viewingSizeKey` is 'all', it resizes all photos. If a photo already exists in the target size,
    //    its edits are maintained. Otherwise, a new photo is created in the target size.
    // 2. If `viewingSizeKey` is a specific size, it resizes only the photos of that size.
    //    If a photo already exists in the target size, its edits are overwritten with the edits
    //    from the source photo, and quantities are merged.
    bulkPhotoResize: (targetSizeKey) => {
      const { viewingSizeKey } = usePrintsUiStore.getState();
      const resizeAll = viewingSizeKey === SizeHelpers.ALL_PHOTOS_KEY;

      set((state) => {
        if (resizeAll) {
          // Scenario 1: Resize all photos, maintaining existing edits in the target size.
          // This ensures that all instances of a root_image_id are consolidated into the targetSizeKey.
          const photosByRootId = new Map();
          state.photos.forEach((p) => {
            if (p.quantity > 0) {
              const group = photosByRootId.get(p.root_image_id) || [];
              group.push(p);
              photosByRootId.set(p.root_image_id, group);
            }
          });

          for (const photoGroup of photosByRootId.values()) {
            const sourcePhotosToConsolidate = photoGroup.filter(
              (p) => p.sizeKey !== targetSizeKey,
            );
            const totalQuantityFromSources = sourcePhotosToConsolidate.reduce(
              (sum, p) => sum + p.quantity,
              0,
            );
            const existingPhotoInTargetSize = photoGroup.find(
              (p) => p.sizeKey === targetSizeKey,
            );

            if (existingPhotoInTargetSize) {
              // If it exists, add quantities from consolidated sources. Edits are preserved.
              if (totalQuantityFromSources > 0) {
                existingPhotoInTargetSize.quantity += totalQuantityFromSources;
                existingPhotoInTargetSize.currentVersion += 1;
              }
              // Mark all original source photos for deletion
              sourcePhotosToConsolidate.forEach((p) => {
                p.quantity = 0;
                p.currentVersion += 1;
              });
            } else if (sourcePhotosToConsolidate.length === 1) {
              // Just move photo into new size, ie set new sizeKey and reset crop
              const photoToMove = sourcePhotosToConsolidate[0];
              photoToMove.sizeKey = targetSizeKey;
              photoToMove.crop = null;
              photoToMove.currentVersion += 1;
            } else if (sourcePhotosToConsolidate.length > 1) {
              // This is a merge into a new size.
              const sourcePhotoTemplate = sourcePhotosToConsolidate[0];
              const newPhoto = {
                ...clonePhotoWithoutEdits(sourcePhotoTemplate),
                id: null,
                image_id: null, // eslint-disable-line camelcase
                root_image_id: sourcePhotoTemplate.root_image_id, // eslint-disable-line camelcase
                original_image_id: sourcePhotoTemplate.original_image_id, // eslint-disable-line camelcase
                sizeKey: targetSizeKey,
                quantity: totalQuantityFromSources,
                make_new_copy: true, // eslint-disable-line camelcase
                client_uuid: uuidv4(), // eslint-disable-line camelcase
                currentVersion: 1,
                savedVersion: 0,
              };
              state.photos.push(newPhoto);

              // Mark all original source photos for deletion
              sourcePhotosToConsolidate.forEach((p) => {
                p.quantity = 0;
                p.currentVersion += 1;
              });
            }
          }
        } else {
          // Scenario 2: Resize only photos matching the viewingSizeKey, overwriting edits.
          const photosToResize = state.photos.filter(
            (p) => p.sizeKey === viewingSizeKey && p.quantity > 0,
          );
          const targetPhotosByRootId = new Map(
            state.photos
              .filter((p) => p.sizeKey === targetSizeKey)
              .map((p) => [p.root_image_id, p]),
          );

          photosToResize.forEach((sourcePhoto) => {
            const targetPhoto = targetPhotosByRootId.get(
              sourcePhoto.root_image_id,
            );

            if (targetPhoto) {
              // Photo exists in target size, merge edits and quantity.
              targetPhoto.quantity += sourcePhoto.quantity;
              PHOTO_ATTRIBUTES.forEach((attr) => {
                if (
                  Object.prototype.hasOwnProperty.call(sourcePhoto, attr) &&
                  !['quantity', 'uploaded'].includes(attr)
                ) {
                  targetPhoto[attr] = sourcePhoto[attr];
                }
              });
              targetPhoto.currentVersion += 1;

              // Mark original photo for deletion.
              sourcePhoto.quantity = 0;
              sourcePhoto.currentVersion += 1;
            } else {
              // Photo doesn't exist in target size, so move it.
              sourcePhoto.sizeKey = targetSizeKey;
              sourcePhoto.crop = null;
              sourcePhoto.currentVersion += 1;
            }
          });
        }
      });

      debouncedSaveOrder();
    },

    // Resizes a single photo from its current size to targetSizeKey. If there is an existing
    // photo with the same root id then it is removed to be replaced by the selected photo.
    singlePhotoResize: ({ clientUuid, targetSizeKey }) => {
      set((state) => {
        const photoToMove = state.photos.find(
          (p) => p.client_uuid === clientUuid,
        );

        if (!photoToMove) return;

        // Reset the crop property as per the old implementation.
        photoToMove.crop = null;

        // Check for and remove a duplicate photo in the target size.
        const duplicateInTarget = state.photos.find(
          (p) =>
            p.sizeKey === targetSizeKey &&
            p.root_image_id === photoToMove.root_image_id,
        );

        if (duplicateInTarget) {
          duplicateInTarget.quantity = 0; // Mark for deletion.
          duplicateInTarget.currentVersion += 1;
        }

        // Move the photo to the new sizeKey and mark as modified.
        photoToMove.sizeKey = targetSizeKey;
        photoToMove.currentVersion += 1;
      });

      debouncedSaveOrder();
    },

    // --- Duplicate Detection ---
    checkForNewDuplicates: () => {
      const { sizesBeforeUpload } = get();
      const {
        features: { printsBuilderDuplicateDetectionDisabled },
      } = usePrintsConfigStore.getState();

      if (
        printsBuilderDuplicateDetectionDisabled ||
        !sizesBeforeUpload.length
      ) {
        return;
      }

      const { photosWithDuplicates: prevDuplicates } =
        listDuplicatePhotos(sizesBeforeUpload);
      const { photos: currentPhotos } = get();
      const { allSizes } = usePrintsConfigStore.getState();
      const { photosWithDuplicates: currentDuplicates } = listDuplicatePhotos(
        selectSizesWithPhotos(allSizes, currentPhotos),
      );

      if (
        JSON.stringify(currentDuplicates) !== JSON.stringify(prevDuplicates)
      ) {
        const { setDuplicateView, setPendingUiEvent } =
          usePrintsUiStore.getState();
        const { closeEditor } = useAdvancedEditorStore.getState();

        // Close advanced edit modal to prevent multiple modals blocking UI
        // TODO: When legacy builder is removed, refactor modal system to ensure one
        // modal at a time
        closeEditor();

        setPendingUiEvent({
          type: UpdatePhotoEvents.SHOW_DUPLICATE_PHOTO_WARNING_MODAL,
          payload: { onReviewButtonAction: setDuplicateView },
        });
      }

      set({ sizesBeforeUpload: [] });
    },
  })),
);
